import React, { useState } from 'react'
import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { useMailboxProviders } from '@/hooks/useMailboxProviders'
import { toast } from 'sonner'
import { Plus, Trash2 } from 'lucide-react'

interface ProviderManagerDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
}

export function ProviderManagerDialog({ open, onOpenChange }: ProviderManagerDialogProps) {
  const { providers, addProvider, refetch } = useMailboxProviders()
  const [newProviderName, setNewProviderName] = useState('')
  const [newProviderDisplayName, setNewProviderDisplayName] = useState('')
  const [isLoading, setIsLoading] = useState(false)

  const handleAddProvider = async () => {
    if (!newProviderName.trim() || !newProviderDisplayName.trim()) {
      toast.error('Please enter both provider name and display name')
      return
    }

    setIsLoading(true)
    try {
      await addProvider({
        name: newProviderName.toLowerCase().replace(/\s+/g, '_'),
        display_name: newProviderDisplayName,
        description: `${newProviderDisplayName} email provider`,
        is_active: true
      })
      
      setNewProviderName('')
      setNewProviderDisplayName('')
      toast.success(`Provider "${newProviderDisplayName}" added successfully`)
    } catch (error) {
      console.error('Failed to add provider:', error)
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Manage Mailbox Providers</DialogTitle>
          <DialogDescription>
            Add new mailbox providers or view existing ones.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6">
          {/* Add New Provider */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Add New Provider</h4>
            <div className="space-y-3">
              <div className="space-y-2">
                <Label htmlFor="provider-name">Provider Name (Internal)</Label>
                <Input
                  id="provider-name"
                  placeholder="e.g., outlook_premium"
                  value={newProviderName}
                  onChange={(e) => setNewProviderName(e.target.value)}
                />
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="provider-display-name">Display Name</Label>
                <Input
                  id="provider-display-name"
                  placeholder="e.g., Outlook Premium"
                  value={newProviderDisplayName}
                  onChange={(e) => setNewProviderDisplayName(e.target.value)}
                />
              </div>
              
              <Button 
                onClick={handleAddProvider} 
                disabled={isLoading}
                className="w-full"
                size="sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                {isLoading ? 'Adding...' : 'Add Provider'}
              </Button>
            </div>
          </div>

          {/* Existing Providers */}
          <div className="space-y-4">
            <h4 className="text-sm font-medium">Current Providers</h4>
            <div className="space-y-2 max-h-48 overflow-y-auto">
              {providers.map((provider) => (
                <div key={provider.id} className="flex items-center justify-between p-2 border rounded-md bg-muted/30">
                  <div>
                    <div className="font-medium text-sm">{provider.display_name}</div>
                    <div className="text-xs text-muted-foreground">{provider.name}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
